import TableOnly from "components/DataTable/TableOnly";
import Spinner from "components/Spinner";
import DeleteModalButton from "components/DeleteModalBtn/DeleteModalButton";
import PaginationWithState from "components/Pagination/PaginationWithState";
import { setNotification } from "features/Notification/notificationSlice";
import React, { useCallback, useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { useDispatch } from "react-redux";
import authHeader from "services/auth-header";
import API from "services/axios";
import { parseFloatData } from "utils/UtilityFuncs";
import SearchableSelect from "components/SearchableSelect/SearchableSelect";

const defaultFilter = { name: "Filter by All", value: null };
const defaultFormState = {
  id: null,
  shipmentId: "",
  name: "",
  totalQty: "",
  assessedValue: "",
  currencyType: "",
  exchangeRate: "",
  // uom: "",
  // uomQty: "",
  assessedValuePkr: "",
};

export default function MShipmentItems() {
  const dispatch = useDispatch();
  const [filterBy, setFilterBy] = useState(defaultFilter);
  const [shipmentItems, setShipmentItems] = useState([]);
  const [shipments, setShipments] = useState([]);
  const [selectedShipmentFilter, setSelectedShipmentFilter] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState(defaultFormState);
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pageLimit, setPageLimit] = useState(10);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");

  const shipmentFilters = [
    { value: "name", name: "Name" },
    { value: "uom", name: "Unit of Measurement" },
    { value: "totalQty", name: "Total Qty" },
    { value: "currencyType", name: "Currency Type" },
    { value: "assessedValue", name: "Assessed Value" },
    { value: "exchangeRate", name: "Exchange Rate" },
    { value: "assessedValuePkr", name: "Assessed Value (PKR)" },
  ];

  /* ******************** All Functions ************************* */

  const resetForm = () => {
    setFormData(defaultFormState);
    setIsUpdateMode(false);
  };

  const buildPayload = () => ({
    ...formData,
    shipmentId: formData.shipmentId ? Number(formData.shipmentId) : null,
    totalQty:
      formData.totalQty === "" || formData.totalQty === null
        ? null
        : Number(parseFloatData(formData.totalQty, 4)),
    assessedValue:
      formData.assessedValue === "" || formData.assessedValue === null
        ? null
        : Number(parseFloatData(formData.assessedValue, 4)),
    exchangeRate:
      formData.exchangeRate === "" || formData.exchangeRate === null
        ? null
        : Number(parseFloatData(formData.exchangeRate, 4)),
    // uomQty:
    //   formData.uomQty === "" || formData.uomQty === null
    //     ? null
    //     : Number(parseFloatData(formData.uomQty, 4)),
    assessedValuePkr:
      formData.assessedValuePkr === "" || formData.assessedValuePkr === null
        ? null
        : Number(parseFloatData(formData.assessedValuePkr, 4)),
  });

  const fetchShipmentOptions = useCallback(async () => {
    try {
      const { data } = await API.post(
        "/manufacturer/shipments?page=1&limit=500",
        { query: "" },
        { headers: authHeader() }
      );
      if (data?.success) {
        setShipments(data?.data || []);
      }
    } catch (err) {
      dispatch(setNotification({ message: err.message, type: "error" }));
    }
  }, [dispatch]);

  const getShipmentItems = useCallback(
    async ({ clearSearch = false, query, page } = {}) => {
      try {
        setLoading(true);
        const usedQuery = clearSearch
          ? ""
          : typeof query !== "undefined"
          ? query
          : "";
        const usedPage = page ?? pageNumber;
        const { data } = await API.post(
          "/manufacturer/shipment-items?page=" +
            usedPage +
            "&limit=" +
            pageLimit,
          {
            query: usedQuery,
            filterBy:
              usedQuery && usedQuery !== "" ? filterBy.value : undefined,
            shipmentId:
              selectedShipmentFilter !== ""
                ? Number(selectedShipmentFilter)
                : undefined,
          },
          { headers: authHeader() }
        );

        if (data?.success) {
          setShipmentItems(data?.data || []);
          setTotalPages(data?.totalPages || 0);
        } else {
          dispatch(
            setNotification({
              message: data?.message || "Failed to fetch",
              type: "error",
            })
          );
        }
      } catch (err) {
        dispatch(setNotification({ message: err.message, type: "error" }));
      } finally {
        setLoading(false);
      }
    },
    [dispatch, filterBy.value, pageLimit, pageNumber, selectedShipmentFilter]
  );

  const deleteShipmentItem = async (id) => {
    try {
      setLoading(true);
      const { data } = await API.delete("/manufacturer/shipment-item/" + id, {
        headers: authHeader(),
      });
      if (data?.success) {
        await getShipmentItems();
        dispatch(
          setNotification({
            message: data?.message || "Deleted",
            type: "success",
          })
        );
        return;
      }
      dispatch(
        setNotification({
          message: data?.message || "Failed to delete",
          type: "error",
        })
      );
    } catch (err) {
      dispatch(setNotification({ message: err.message, type: "error" }));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateItem = async (e) => {
    e.preventDefault();
    if (!formData.shipmentId) {
      dispatch(
        setNotification({
          message: "Please select a shipment before adding an item.",
          type: "error",
        })
      );
      return;
    }
    try {
      setLoading(true);
      const { data } = await API.post(
        "/manufacturer/shipment-item",
        buildPayload(),
        { headers: authHeader() }
      );
      if (data?.success) {
        resetForm();
        setShowModal(false);
        setPageNumber(1);
        await getShipmentItems({ page: 1 });
        dispatch(
          setNotification({
            message: data?.message || "Item created",
            type: "success",
          })
        );
      } else {
        dispatch(setNotification({ message: data?.message, type: "error" }));
      }
    } catch (err) {
      dispatch(setNotification({ message: err.message, type: "error" }));
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateItem = async (e) => {
    e.preventDefault();
    if (!formData.shipmentId) {
      dispatch(
        setNotification({
          message: "Please select a shipment before updating the item.",
          type: "error",
        })
      );
      return;
    }
    try {
      setLoading(true);
      const { data } = await API.patch(
        "/manufacturer/shipment-item",
        buildPayload(),
        { headers: authHeader() }
      );
      if (data?.success) {
        resetForm();
        setShowModal(false);
        await getShipmentItems();
        dispatch(
          setNotification({
            message: data?.message || "Item updated",
            type: "success",
          })
        );
      } else {
        dispatch(setNotification({ message: data?.message, type: "error" }));
      }
    } catch (err) {
      dispatch(setNotification({ message: err.message, type: "error" }));
    } finally {
      setLoading(false);
    }
  };

  const handleEditItem = (item) => {
    setFormData({
      id: item.id,
      shipmentId: item.shipmentId ? String(item.shipmentId) : "",
      name: item.name || "",
      totalQty: item.totalQty || "",
      uom: item.uom || "",
      assessedValue: item.assessedValue || "",
      currencyType: item.currencyType || "",
      exchangeRate: item.exchangeRate || "",
      // uom: item.uom || "",
      // uomQty: item.uomQty === 0 || item.uomQty ? String(item.uomQty) : "",
      assessedValuePkr:
        item.assessedValuePkr === 0 || item.assessedValuePkr
          ? String(item.assessedValuePkr)
          : "",
    });
    setIsUpdateMode(true);
    setShowModal(true);
  };

  const searchFunction = async (searchText, page, limit) => {
    try {
      const { data } = await API.post(
        "/manufacturer/shipments?page=" + page + "&limit=" + limit,
        {
          query: searchText,
          notExpired: true,
          // filterBy:
          //   searchText && searchText !== "" ? filterBy.value : undefined,
          // ...dateFilters,
        },
        { headers: authHeader() }
      );
      return { results: data.data, total: data.total };
    } catch (error) {
      console.error("Error fetching search results:", error);
      return { results: [], total: 0 };
    }
  };

  const getShipmentLabel = (ship) =>
    `${ship.shipmentId}${ship.b_e_no ? ` - ${ship.b_e_no}` : ""}`;

  const selectedShipmentFilterLabel =
    selectedShipmentFilter !== ""
      ? (() => {
          const ship = shipments.find(
            (s) => String(s.shipmentId) === selectedShipmentFilter
          );
          return ship ? getShipmentLabel(ship) : "";
        })()
      : "";

  /* ******************** All UseEffects ************************* */

  useEffect(() => {
    fetchShipmentOptions();
  }, [fetchShipmentOptions]);

  // Auto-calculate assessedValuePkr when assessedValue or exchangeRate changes
  useEffect(() => {
    const assessed = parseFloat(
      formData.assessedValue !== "" && formData.assessedValue !== null
        ? parseFloatData(formData.assessedValue, 4)
        : "0"
    );
    const rate = parseFloat(
      formData.exchangeRate !== "" && formData.exchangeRate !== null
        ? parseFloatData(formData.exchangeRate, 4)
        : "0"
    );
    if (isNaN(assessed) || isNaN(rate)) {
      setFormData((prev) => ({ ...prev, assessedValuePkr: "" }));
      return;
    }
    const total = assessed * rate;
    if (!isNaN(total)) {
      setFormData((prev) => ({
        ...prev,
        assessedValuePkr: total === 0 ? "0" : parseFloatData(String(total), 4),
      }));
    }
  }, [formData.assessedValue, formData.exchangeRate]);

  useEffect(() => {
    let timeout = setTimeout(() => {
      getShipmentItems(
        searchTerm && searchTerm !== ""
          ? { query: searchTerm, page: pageNumber }
          : { clearSearch: true, page: pageNumber }
      );
    }, 50);
    return () => clearTimeout(timeout);
  }, [filterBy, pageLimit, pageNumber, selectedShipmentFilter]);

  const filterDropdown = () => (
    <div className="d-flex justify-content-end">
      <button
        type="button"
        className="px-1 bg-primary btn text-dark bg-opacity-10 border border-primary rounded-end mx-2 rounded-start"
        data-bs-toggle="dropdown"
        aria-haspopup="true"
        aria-expanded="false"
      >
        {filterBy ? filterBy.name : "Filter by All"}
      </button>
      <div className="dropdown-menu">
        <button
          onClick={() => setFilterBy(defaultFilter)}
          className="dropdown-item"
        >
          Filter by All
        </button>
        {shipmentFilters.map((item, index) => (
          <button
            key={index}
            onClick={() => setFilterBy(item)}
            className="dropdown-item"
          >
            {item.name}
          </button>
        ))}
      </div>
    </div>
  );

  const table_head = [
    { name: "Shipment", value: "shipment.b_e_no" },
    { name: "Name", value: "name" },
    { name: "Total Qty", value: "totalQty" },
    { name: "Assessed Value", value: "assessedValue" },
    { name: "Exchange Rate", value: "exchangeRate" },
    { name: "Assessed Value (PKR)", value: "assessedValuePkr" },
    // { name: "UOM Qty", value: "uomQty" },
    { name: "Actions", value: "actions" },
  ];

  const table_body = (item) => (
    <tr key={item.id}>
      <td className="align-middle py-1">
        {item.shipment ? item.shipment.b_e_no || "-" : "-"}
      </td>
      <td className="align-middle py-1">{item.name || "-"}</td>
      <td className="align-middle py-1">
        {parseFloatData(item.totalQty, 4) || "-"} {item.uom || "-"}
      </td>
      <td className="align-middle py-1">
        {item.currencyType || "-"}{" "}
        {parseFloatData(item.assessedValue, 4) || "-"}
      </td>
      <td className="align-middle py-1">{item.exchangeRate || "-"}</td>
      <td className="align-middle py-1">
        {parseFloatData(item.assessedValuePkr, 4) ?? "-"}
      </td>
      {/* <td className="align-middle py-1">
        {item.uomQty ?? "-"} {item.uom || "-"}
      </td> */}
      <td className="align-middle py-1 text-end table-action">
        <button
          disabled={loading}
          onClick={() => handleEditItem(item)}
          className="btn btn-sm px-1"
        >
          <i className="mdi mdi-pencil"></i>
        </button>
        <DeleteModalButton
          btnClass={"btn btn-sm px-1"}
          deleteFunc={deleteShipmentItem}
          deleteParams={item.id}
          disableConditions={loading}
        />
      </td>
    </tr>
  );

  return (
    <div className="">
      <h2 className="mb-3 ps-2">Imported Items</h2>
      {loading && <Spinner />}

      <div className="d-flex justify-content-between mb-3 align-items-center flex-wrap gap-3">
        <div className="ps-2">
          Show &nbsp;
          <div className="btn-group">
            <button
              className="px-1 bg-primary bg-opacity-10 border border-primary rounded-end rounded-start"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              {pageLimit}
            </button>
            <ul className="dropdown-menu w-25">
              {[5, 10, 15, 20].map((limit) => (
                <li key={limit} onClick={() => setPageLimit(limit)}>
                  <button className="dropdown-item">{limit}</button>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="d-flex align-items-end flex-wrap justify-content-end gap-2">
          <div className="d-flex flex-column">
            <small className="text-muted mb-1">Filter by Shipment</small>
            <SearchableSelect
              placeholder="Search shipments..."
              customClasses="form-select form-select-sm"
              width="220px"
              searchFunction={searchFunction}
              selectedLabel={selectedShipmentFilterLabel}
              ResetComponent={({ close }) => (
                <div
                  className={`px-3 py-1 m-1 rounded-3 ${
                    selectedShipmentFilter === ""
                      ? "bg-primary text-white"
                      : "bg-light"
                  }`}
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    setPageNumber(1);
                    setSelectedShipmentFilter("");
                    close();
                  }}
                >
                  All Shipments
                </div>
              )}
              RenderItem={({ item }) => (
                <div
                  className={`px-3 py-1 m-1 rounded-3 ${
                    String(item.shipmentId) === selectedShipmentFilter
                      ? "bg-primary text-white"
                      : "bg-light"
                  }`}
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    setPageNumber(1);
                    setSelectedShipmentFilter(String(item.shipmentId));
                  }}
                >
                  {item.shipmentId}
                  {item.b_e_no ? ` - ${item.b_e_no}` : ""}
                </div>
              )}
            />
          </div>
          <div>{filterDropdown()}</div>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              setPageNumber(1);
              if (searchTerm && searchTerm !== "") {
                getShipmentItems({ query: searchTerm, page: 1 });
              } else {
                getShipmentItems({ clearSearch: true, page: 1 });
              }
            }}
            className="d-flex justify-content-end gap-1 align-items-center"
          >
            <input
              type="search"
              placeholder="Search items..."
              value={searchTerm}
              className="form-control"
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button className="btn btn-info" type="submit">
              Search
            </button>
            {searchTerm !== "" && (
              <button
                className="btn btn-outline-danger"
                type="button"
                onClick={() => {
                  setSearchTerm("");
                  getShipmentItems({ clearSearch: true, page: 1 });
                }}
              >
                Clear
              </button>
            )}
          </form>
          <div>
            <button
              onClick={() => {
                resetForm();
                setShowModal(true);
              }}
              type="button"
              className="btn btn-primary"
              disabled={!shipments.length}
            >
              Add Shipment Item
            </button>
          </div>
        </div>
      </div>

      <div
        className="px-1 flex-grow-1 d-flex"
        style={{ minHeight: "50vh", borderRadius: 15 }}
      >
        <div className="horizontal-scrollable d-flex flex-grow-1">
          <TableOnly
            table_head={table_head}
            table_body={table_body}
            table_data={shipmentItems}
            disableSorting={true}
            actionsOnEnd={true}
          />
        </div>
      </div>

      <div className="d-flex justify-content-end mt-2">
        <PaginationWithState
          totalPages={totalPages}
          currPage={pageNumber}
          setCurrPage={setPageNumber}
        />
      </div>

      <Modal
        show={showModal}
        onHide={() => {
          setShowModal(false);
          resetForm();
        }}
      >
        <Modal.Body
          className="shadow-lg bg-white px-3"
          style={{ borderRadius: 15 }}
        >
          <div className="">
            <h4>
              {isUpdateMode ? "Update Imported Item" : "Add Imported Item"}
            </h4>
          </div>
          <hr className="mt-0" />
          <form onSubmit={isUpdateMode ? handleUpdateItem : handleCreateItem}>
            <div className="mb-2">
              <label className="">Shipment</label>
              <SearchableSelect
                placeholder="Select shipment..."
                customClasses="form-select"
                searchFunction={searchFunction}
                selectedLabel={(() => {
                  if (!formData.shipmentId) return "";
                  const ship = shipments.find(
                    (s) => String(s.shipmentId) === formData.shipmentId
                  );
                  return ship ? getShipmentLabel(ship) : "";
                })()}
                RenderItem={({ item }) => (
                  <div
                    className={`px-3 py-1 m-1 rounded-3 ${
                      String(item.shipmentId) === formData.shipmentId
                        ? "bg-primary text-white"
                        : "bg-light"
                    }`}
                    style={{ cursor: "pointer" }}
                    onClick={() =>
                      setFormData({
                        ...formData,
                        shipmentId: String(item.shipmentId),
                      })
                    }
                  >
                    {item.shipmentId}
                    {item.b_e_no ? ` - ${item.b_e_no}` : ""}
                  </div>
                )}
              />
            </div>

            <div className="mb-2">
              <label className="">Name</label>
              <input
                type="text"
                className="form-control"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                placeholder="Enter name"
                required
              />
            </div>
            <div className="mb-2">
              <label className="">Unit Of Measurement</label>
              <input
                type="text"
                className="form-control"
                value={formData.uom}
                onChange={(e) =>
                  setFormData({ ...formData, uom: e.target.value })
                }
                placeholder="Enter UOM"
              />
            </div>
            <div className="mb-2">
              <label className="">Total Qty</label>
              <input
                type="number"
                step="0.0001"
                min="0"
                className="form-control"
                value={formData.totalQty}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    totalQty: parseFloatData(e.target.value, 4),
                  })
                }
                placeholder="Enter total quantity"
                required
              />
            </div>

            <div className="mb-2">
              <label className="">Currency Type</label>
              <input
                type="text"
                className="form-control"
                value={formData.currencyType}
                onChange={(e) =>
                  setFormData({ ...formData, currencyType: e.target.value })
                }
                placeholder="Enter currency"
              />
            </div>
            <div className="mb-2">
              <label className="">Assessed Value</label>
              <input
                type="number"
                step="0.0001"
                min="0"
                className="form-control"
                value={formData.assessedValue}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    assessedValue: parseFloatData(e.target.value, 4),
                  })
                }
                placeholder="Enter assessed value"
              />
            </div>

            <div className="mb-2">
              <label className="">Exchange Rate</label>
              <input
                type="number"
                step="0.0001"
                min="0"
                className="form-control"
                value={formData.exchangeRate}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    exchangeRate: parseFloatData(e.target.value, 4),
                  })
                }
                placeholder="Enter exchange rate"
              />
            </div>
            <div className="mb-2">
              <label className="">Assessed Value (PKR)</label>
              <input
                type="text"
                className="form-control"
                value={formData.assessedValuePkr}
                readOnly
                disabled
              />
            </div>
            {/*
            <div className="mb-2">
              <label className="">Unit of Measurement</label>
              <input
                type="text"
                className="form-control"
                value={formData.uom}
                onChange={(e) =>
                  setFormData({ ...formData, uom: e.target.value })
                }
                placeholder="Enter UOM"
              />
            </div>
            <div className="mb-2">
              <label className="">UOM Qty</label>
              <input
                type="number"
                step="0.0001"
                min="0"
                className="form-control"
                value={formData.uomQty}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    uomQty: parseFloatData(e.target.value, 4),
                  })
                }
                placeholder="Enter UOM quantity"
              />
            </div>
            */}

            <div className="mt-3">
              <div className="d-flex justify-content-end">
                <button
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="btn btn-secondary btn-sm"
                  type="button"
                >
                  Close
                </button>
                <button
                  disabled={loading}
                  className="btn btn-primary btn-sm ms-1"
                  type="submit"
                >
                  {isUpdateMode ? "Update" : "Add"}
                </button>
              </div>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </div>
  );
}
